import type { BillingRecord, Plan, Subscription } from '../../types';
import { addMonths, addDays, formatISO } from 'date-fns';
import { billingHistory, occupations } from '../mock/data';
import { delay } from '../mock/delay';

let currentSubscription: Subscription | null = null;

export const subscriptionService = {
  async getActive(): Promise<Subscription | null> {
    return delay(currentSubscription);
  },

  async create(plan: Plan, occupationId: string, freeTrial: boolean): Promise<Subscription> {
    const now = new Date();
    const occupation = occupations.find((o) => o.id === occupationId);
    const sub: Subscription = {
      id: `sub_${Math.random().toString(36).slice(2, 10)}`,
      planId: plan.id,
      planName: plan.name,
      occupationId,
      occupationTitle: occupation?.title ?? 'Occupation',
      status: freeTrial ? 'trialing' : 'active',
      price: plan.price,
      interval: plan.interval,
      startedAt: formatISO(now),
      nextBillingDate: formatISO(
        freeTrial && plan.freeTrialDays ? addDays(now, plan.freeTrialDays) : addMonths(now, 1),
      ),
      trialEndsAt:
        freeTrial && plan.freeTrialDays ? formatISO(addDays(now, plan.freeTrialDays)) : undefined,
      cancelAtPeriodEnd: false,
    };
    currentSubscription = sub;
    return delay(sub, 600);
  },

  async upgrade(plan: Plan): Promise<Subscription> {
    if (!currentSubscription) throw new Error('No active subscription');
    currentSubscription = {
      ...currentSubscription,
      planId: plan.id,
      planName: plan.name,
      price: plan.price,
      status: 'active',
    };
    return delay(currentSubscription, 600);
  },

  async cancel(): Promise<Subscription> {
    if (!currentSubscription) throw new Error('No active subscription');
    currentSubscription = { ...currentSubscription, cancelAtPeriodEnd: true, status: 'canceled' };
    return delay(currentSubscription, 600);
  },

  async getBillingHistory(): Promise<BillingRecord[]> {
    return delay(billingHistory);
  },
};
